import Dexie from 'dexie';

export const db = new Dexie('ShikshaEdgeDB');

if (typeof window !== 'undefined') {
  window.db = db;
}

db.version(1).stores({
  students:  '++id, rollNo, name, class, section, guardianName',
  attendance: '++id, studentId, date, status, syncStatus',
  marks:     '++id, studentId, subject, term, score, maxScore, syncStatus',
  syncQueue: '++id, table, recordId, operation, payload, timestamp',
});

db.version(2).stores({
  students:  '++id, rollNo, name, class, section, guardianName',
  attendance: '++id, studentId, date, status, syncStatus',
  marks:     '++id, studentId, subject, term, componentId, score, maxScore, syncStatus',
  syncQueue: '++id, table, recordId, operation, payload, timestamp',
});

db.version(3).stores({
  students:  '++id, rollNo, name, class, section, guardianName',
  attendance: '++id, studentId, date, status, syncStatus',
  marks:     '++id, studentId, subject, term, componentId, score, maxScore, syncStatus',
  syncQueue: '++id, table, recordId, operation, payload, timestamp',
  teachers:  '++id, email, name, role',
  schemas:   '++id, cohort, xml, code, updatedAt'
});

db.version(4).stores({
  students:  '++id, rollNo, name, class, section, guardianName',
  attendance: '++id, studentId, date, status, syncStatus',
  marks:     '++id, studentId, subject, term, componentId, score, maxScore, syncStatus',
  syncQueue: '++id, table, recordId, operation, payload, timestamp',
  teachers:  '++id, email, name, role, isClassTeacher, classTeacherOf',
  schemas:   '++id, cohort, xml, code, updatedAt'
});

// Version 5: Add settings, activityLog, co-scholastic grades, and extended student fields
db.version(5).stores({
  students:      '++id, rollNo, name, class, section, guardianName, dob, gender, motherName',
  attendance:    '++id, studentId, date, status, syncStatus',
  marks:         '++id, studentId, subject, term, componentId, score, maxScore, syncStatus',
  syncQueue:     '++id, table, recordId, operation, payload, timestamp',
  teachers:      '++id, email, name, role, isClassTeacher, classTeacherOf',
  schemas:       '++id, cohort, xml, code, updatedAt',
  settings:      '++id, key, value, updatedAt',
  activityLog:   '++id, action, details, timestamp',
  coScholastic:  '++id, studentId, area, grade, term, academicYear',
});

// Version 6: Add DPDP Consent tracking for admins
db.version(6).stores({
  students:      '++id, rollNo, name, class, section, guardianName, dob, gender, motherName',
  attendance:    '++id, studentId, date, status, syncStatus',
  marks:         '++id, studentId, subject, term, componentId, score, maxScore, syncStatus',
  syncQueue:     '++id, table, recordId, operation, payload, timestamp',
  teachers:      '++id, email, name, role, isClassTeacher, classTeacherOf',
  schemas:       '++id, cohort, xml, code, updatedAt',
  settings:      '++id, key, value, updatedAt',
  activityLog:   '++id, action, details, timestamp',
  coScholastic:  '++id, studentId, area, grade, term, academicYear',
  dpdpConsent:   '++id, adminEmail, timestamp, status'
});

// Version 7: Add academicStatus to students for rollover logic
db.version(7).stores({
  students:      '++id, rollNo, name, class, section, guardianName, dob, gender, motherName, academicStatus',
  attendance:    '++id, studentId, date, status, syncStatus',
  marks:         '++id, studentId, subject, term, componentId, score, maxScore, syncStatus',
  syncQueue:     '++id, table, recordId, operation, payload, timestamp',
  teachers:      '++id, email, name, role, isClassTeacher, classTeacherOf',
  schemas:       '++id, cohort, xml, code, updatedAt',
  settings:      '++id, key, value, updatedAt',
  activityLog:   '++id, action, details, timestamp',
  coScholastic:  '++id, studentId, area, grade, term, academicYear',
  dpdpConsent:   '++id, adminEmail, timestamp, status'
});

// Version 8: Add syncHistory and batchId for offline undo
db.version(8).stores({
  students:      '++id, rollNo, name, class, section, guardianName, dob, gender, motherName, academicStatus',
  attendance:    '++id, studentId, date, status, syncStatus, batchId',
  marks:         '++id, studentId, subject, term, componentId, score, maxScore, syncStatus, batchId',
  syncQueue:     '++id, table, recordId, operation, payload, timestamp, batchId',
  syncHistory:   '++id, batchId, timestamp, description',
  teachers:      '++id, email, name, role, isClassTeacher, classTeacherOf',
  schemas:       '++id, cohort, xml, code, updatedAt',
  settings:      '++id, key, value, updatedAt',
  activityLog:   '++id, action, details, timestamp',
  coScholastic:  '++id, studentId, area, grade, term, academicYear',
  dpdpConsent:   '++id, adminEmail, timestamp, status'
});

export const SYNC_STATUS = {
  LOCAL:    'local',
  QUEUED:   'queued',
  SYNCED:   'synced',
  CONFLICT: 'conflict',
};

/**
 * Enqueue a record change for eventual sync to the Cloudflare Worker.
 * @param {string} table - Source table name ('attendance' | 'marks')
 * @param {number} recordId - The Dexie auto-increment ID of the record
 * @param {string} operation - 'create' | 'update' | 'delete'
 * @param {Object} payload - The data snapshot to sync
 * @param {string} [batchId] - Optional batch identifier for undo
 */
export async function addToSyncQueue(table, recordId, operation, payload, batchId = null) {
  const academicYear = await getSetting('academicYear') || 'UNKNOWN';
  
  await db.syncQueue.add({
    table,
    recordId,
    operation,
    payload,
    batchId,
    academicYear,
    schemaVersion: db.verno,
    timestamp: Date.now(),
  });

  // Log the activity
  try {
    await db.activityLog.add({
      action: operation,
      details: `${operation} ${table} record #${recordId}`,
      timestamp: Date.now(),
    });
  } catch {
    // activityLog may not exist in older DB versions — ignore
  }
}

/**
 * Record a submission event to history for undo capabilities.
 * @param {string} batchId - The batch identifier
 * @param {string} description - User-friendly description of the batch
 */
export async function recordHistoryBatch(batchId, description) {
  try {
    await db.syncHistory.add({
      batchId,
      description,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error('Failed to log syncHistory:', error);
  }
}

/**
 * @returns {Promise<number>} Count of items waiting in the sync queue
 */
export async function getPendingSyncCount() {
  return db.syncQueue.count();
}

/**
 * Get or set a setting value.
 * @param {string} key
 * @param {string} [value] - If provided, sets the setting
 * @returns {Promise<string|null>}
 */
export async function getSetting(key) {
  const entry = await db.settings.where('key').equals(key).first();
  return entry?.value ?? null;
}

export async function setSetting(key, value) {
  const existing = await db.settings.where('key').equals(key).first();
  if (existing) {
    await db.settings.update(existing.id, { value, updatedAt: Date.now() });
  } else {
    await db.settings.add({ key, value, updatedAt: Date.now() });
  }
}
